import React, { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import axios from "axios";
import { 
  Landmark,
  TrendingUp,
  RefreshCw,
  CheckCircle,
  XCircle,
  IndianRupee,
  Percent,
  Calendar,
  PiggyBank,
  ArrowRight
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";

export const FDvsDebtPage = () => {
  const [fdRate, setFdRate] = useState(7.0);
  const [investment, setInvestment] = useState(100000);
  const [years, setYears] = useState(3);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchComparison = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/funds/analytics/fd-vs-debt?fd_rate=${fdRate}&investment=${investment}&years=${years}`
      );
      setResults(response.data);
    } catch (error) {
      console.error("Error fetching comparison:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComparison();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const chartData = results ? [
    {
      name: `FD @ ${fdRate}%`,
      value: results.fd_details.post_tax_return,
      color: '#64748B'
    },
    ...results.debt_funds.slice(0, 5).map((fund, idx) => ({
      name: fund.scheme_name.substring(0, 15) + '...',
      value: fund.post_tax_gain,
      color: fund.beats_fd ? '#22C55E' : '#EF4444'
    }))
  ] : [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="fd-vs-debt-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Landmark className="w-8 h-8 text-primary" />
            FD vs Debt Funds
          </h1>
          <p className="text-muted-foreground mt-1">
            Compare Fixed Deposit returns with Debt Mutual Funds
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Panel */}
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle>Investment Parameters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Investment Amount */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <IndianRupee className="w-4 h-4" />
                  Investment Amount
                </Label>
                <Input
                  type="number"
                  value={investment}
                  onChange={(e) => setInvestment(Number(e.target.value))}
                  min={10000}
                  step={10000}
                />
                <div className="flex gap-2">
                  {[100000, 500000, 1000000, 2500000].map(amt => (
                    <Button
                      key={amt}
                      variant={investment === amt ? "default" : "outline"}
                      size="sm"
                      onClick={() => setInvestment(amt)}
                    >
                      {amt >= 100000 ? `${amt/100000}L` : `${amt/1000}K`}
                    </Button>
                  ))}
                </div>
              </div>

              {/* FD Rate */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Percent className="w-4 h-4" />
                  FD Interest Rate: {fdRate}%
                </Label>
                <Slider
                  value={[fdRate]}
                  onValueChange={([v]) => setFdRate(v)}
                  min={5}
                  max={9}
                  step={0.25}
                  className="mt-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>5%</span>
                  <span>9%</span>
                </div>
              </div>

              {/* Investment Period */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Investment Period
                </Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 5, 7].map(y => (
                    <Button
                      key={y}
                      variant={years === y ? "default" : "outline"}
                      size="sm"
                      onClick={() => setYears(y)}
                    >
                      {y}Y
                    </Button>
                  ))}
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={fetchComparison}
                disabled={loading}
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TrendingUp className="w-4 h-4 mr-2" />
                )}
                Compare Returns
              </Button>
            </CardContent>
          </Card>

          {/* Results Panel */}
          <div className="lg:col-span-2 space-y-6">
            {results && (
              <>
                {/* FD Card */}
                <Card className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 border-slate-600/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-slate-600 flex items-center justify-center">
                        <Landmark className="w-6 h-6 text-slate-300" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">Fixed Deposit</h3>
                        <p className="text-sm text-muted-foreground">Bank FD @ {fdRate}% p.a.</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-sm text-muted-foreground">Invested</div>
                        <div className="text-lg font-bold">{formatCurrency(investment)}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Maturity Value</div>
                        <div className="text-lg font-bold text-primary">
                          {formatCurrency(results.fd_details.maturity_value)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Interest Earned</div>
                        <div className="text-lg font-bold text-green-500">
                          {formatCurrency(results.fd_details.interest_earned)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Post-Tax Return</div>
                        <div className="text-lg font-bold text-green-400">
                          {formatCurrency(results.fd_details.post_tax_return)}
                        </div>
                        <div className="text-xs text-muted-foreground">(30% tax slab)</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Chart */}
                <Card className="bg-card/50 border-border/50">
                  <CardHeader>
                    <CardTitle>Post-Tax Returns Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis type="number" stroke="#9CA3AF" tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                          <YAxis type="category" dataKey="name" stroke="#9CA3AF" width={120} tick={{ fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #374151' }}
                            formatter={(value) => [formatCurrency(value), 'Post-Tax Gain']}
                          />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Debt Funds Table */}
                <Card className="bg-card/50 border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PiggyBank className="w-5 h-5 text-primary" />
                      Top Debt Mutual Funds
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3 font-medium">Fund Name</th>
                            <th className="text-right p-3 font-medium">Historical CAGR</th>
                            <th className="text-right p-3 font-medium">Projected Value</th>
                            <th className="text-right p-3 font-medium">Post-Tax Gain</th>
                            <th className="text-center p-3 font-medium">vs FD</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.debt_funds.map((fund) => (
                            <tr key={fund.scheme_code} className="border-t border-border/50">
                              <td className="p-3">
                                <div className="font-medium text-sm">{fund.scheme_name}</div>
                              </td>
                              <td className="p-3 text-right">
                                <span className={fund.historical_cagr >= fdRate ? 'text-green-500' : 'text-muted-foreground'}>
                                  {fund.historical_cagr}%
                                </span>
                              </td>
                              <td className="p-3 text-right">{formatCurrency(fund.projected_value)}</td>
                              <td className={`p-3 text-right font-bold ${fund.post_tax_gain >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {formatCurrency(fund.post_tax_gain)}
                              </td>
                              <td className="p-3 text-center">
                                {fund.beats_fd ? (
                                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                    <CheckCircle className="w-3 h-3 mr-1" /> Better
                                  </Badge>
                                ) : (
                                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                    <XCircle className="w-3 h-3 mr-1" /> Lower
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Disclaimer */}
                <Card className="bg-yellow-500/10 border-yellow-500/20">
                  <CardContent className="p-4 text-sm text-yellow-200">
                    <strong>Note:</strong> {results.note} Debt fund returns shown are historical CAGR and may vary. 
                    Long-term capital gains on debt funds held for more than 3 years are taxed at 20% with indexation benefit.
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
